import React, { useEffect, useState } from 'react';
import AdminTable from 'common/AdminTable';
import { getRequest, deleteRequest, patchRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const exportToExcel = (data) => {
  const headers = ['S.No', 'Name', 'Email', 'Mobile', 'City', 'Enquiry Type', 'Status', 'Date'];
  const rows = data.map((row, i) => [
    i + 1,
    row.name || '',
    row.mail || '',
    row.mobile || '',
    row.city || '',
    row.enquiry_type || '',
    row.is_active ? 'Active' : 'Inactive',
    formatDate(row.createdAt),
  ]);

  let html = '<html><head><meta charset="UTF-8"><title>Contact Us Export</title></head><body>';
  html += '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse:collapse;font-family:Arial;font-size:13px">';
  html += '<thead><tr style="background:#4F81BD;color:#fff">';
  headers.forEach((h) => { html += `<th>${h}</th>`; });
  html += '</tr></thead><tbody>';
  rows.forEach((row) => {
    html += '<tr>';
    row.forEach((cell) => { html += `<td>${cell}</td>`; });
    html += '</tr>';
  });
  html += '</tbody></table></body></html>';

  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const matchFilter = (item, mode, dateStr, monthStr, yearStr) => {
  if (mode === 'all') return true;
  const d = new Date(item.createdAt);
  if (isNaN(d.getTime())) return false;
  const itemYear = d.getFullYear();
  const itemMonth = d.getMonth() + 1;
  const itemDay = d.getDate();

  if (mode === 'date') {
    if (!dateStr) return true;
    const [y, m, day] = dateStr.split('-').map(Number);
    return itemYear === y && itemMonth === m && itemDay === day;
  }
  if (mode === 'month') {
    if (!monthStr) return true;
    const [y, m] = monthStr.split('-').map(Number);
    return itemYear === y && itemMonth === m;
  }
  if (mode === 'year') {
    if (!yearStr) return true;
    return itemYear === Number(yearStr);
  }
  return true;
};

const ListCustomer = () => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 10;

  const [filterMode, setFilterMode] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const years = [];
  for (let y = 2020; y <= new Date().getFullYear() + 1; y++) years.push(y);

  const fetchData = async (pageNum) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      const res = await getRequest(`contactus?page=${pageNum}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setAllData(res.data.data);
        setTotalPages(res.data.totalPages);
        setTotalItems(res.data.total);
        setPage(res.data.page);
      } else {
        toast.error(res.data.message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  useEffect(() => {
    const filtered = allData.filter((item) =>
      matchFilter(item, filterMode, selectedDate, selectedMonth, selectedYear)
    );
    setFilteredData(filtered);
  }, [allData, filterMode, selectedDate, selectedMonth, selectedYear]);

  const applyFilter = (mode, date, month, year) => {
    setFilterMode(mode);
    setSelectedDate(date);
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  const referCheck = () => {
    fetchData(page);
  };

  const handleBulkDelete = async () => {
    if (selectedCustomerIds.length === 0) return;
    if (window.confirm(`Delete ${selectedCustomerIds.length} selected customer(s)?`)) {
      setLoading(true);
      try {
        const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
        for (const id of selectedCustomerIds) {
          await deleteRequest(`contactus/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        if (allData.length === selectedCustomerIds.length && page > 1) {
          fetchData(page - 1);
        } else {
          fetchData(page);
        }
        setSelectedCustomerIds([]);
        toast.success(`${selectedCustomerIds.length} customer(s) deleted successfully`);
      } catch (error) {
        toast.error('Error deleting customers');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkUpdateStatus = async () => {
    if (selectedCustomerIds.length === 0) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      for (const id of selectedCustomerIds) {
        await patchRequest(`contactus/${id}`, { is_active: bulkStatus }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      fetchData(page);
      setSelectedCustomerIds([]);
      setShowBulkModal(false);
      toast.success(`${selectedCustomerIds.length} customer(s) status updated`);
    } catch (error) {
      toast.error('Error updating status');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) fetchData(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) fetchData(page + 1);
  };

  const displayData = filterMode === 'all' ? allData : filteredData;

  const tableHeaders = [
    { Header: 'S.No', accessor: 'sno', Cell: ({ row }) => row.index + 1 + (page - 1) * limit },
    { Header: 'Name', accessor: 'name' },
    { Header: 'Email', accessor: 'mail' },
    { Header: 'Mobile', accessor: 'mobile' },
    { Header: 'City', accessor: 'city' },
    {
      Header: 'Status',
      accessor: 'is_active',
      Cell: ({ row }) => (
        <span className={row.original.is_active ? 'badge badge-success' : 'badge badge-danger'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    { Header: 'Date', accessor: 'createdAt', Cell: ({ row }) => formatDate(row.original.createdAt) },
  ];

  if (loading) return <div className="text-center mt-5">Loading...</div>;

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4 d-flex align-items-center">
        <h4 className="font-weight-bold">Customer List</h4>
        <button className="ml-auto btn btn-primary mr-2" onClick={referCheck}>
          <i className="fa fa-refresh" aria-hidden="true"></i>
        </button>
      </div>

      <div className="mx-4 my-3 p-3 d-flex align-items-center flex-wrap gap-2" style={{ background: '#f8f9fa', borderRadius: 8 }}>
        <div className="btn-group btn-group-sm mr-3" role="group">
          <button className={`btn ${filterMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => applyFilter('all', '', '', '')}>All</button>
          <button className={`btn ${filterMode === 'date' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => applyFilter('date', '', '', '')}>Date</button>
          <button className={`btn ${filterMode === 'month' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => applyFilter('month', '', '', '')}>Month</button>
          <button className={`btn ${filterMode === 'year' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => applyFilter('year', '', '', '')}>Year</button>
        </div>

        {filterMode === 'date' && (
          <input type="date" className="form-control" style={{ maxWidth: 200 }} value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
        )}
        {filterMode === 'month' && (
          <input type="month" className="form-control" style={{ maxWidth: 200 }} value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
        )}
        {filterMode === 'year' && (
          <select className="form-control" style={{ width: 120 }} value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            <option value="">Select Year</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}

        <button className="btn btn-success btn-sm ml-auto" onClick={() => exportToExcel(displayData)}>
          <i className="fa fa-file-excel-o mr-1" aria-hidden="true"></i> Export
        </button>
      </div>

      {
        selectedCustomerIds.length > 0 && (
          <div className="mx-4 my-3 d-flex gap-2">
            <button className="btn btn-danger" onClick={handleBulkDelete}>
              Delete Selected ({selectedCustomerIds.length})
            </button>
            <button className="btn btn-warning" onClick={() => setShowBulkModal(true)}>
              Bulk Update Status
            </button>
          </div>
        )
      }

      {
        displayData.length > 0 ? (
          <>
            <AdminTable
              tableHeaders={tableHeaders}
              tableData={displayData}
              showCheckbox={true}
              onSelectionChange={setSelectedCustomerIds}
              rowIdKey="id"
            />
            <div className="d-flex justify-content-between align-items-center mt-4 mx-4">
              <div>Showing {displayData.length} of {totalItems} entries</div>
              <div>
                <button className="btn btn-secondary mx-1" onClick={handlePrevPage} disabled={page === 1}>
                  Previous
                </button>
                <span className="mx-2">Page {page} of {totalPages}</span>
                <button className="btn btn-secondary mx-1" onClick={handleNextPage} disabled={page === totalPages}>
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <p className="container-fluid bg-danger w-100 p-2 mx-4 text-center text-white">No data found</p>
        )
      }

      {
        showBulkModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Bulk Update Status</h5>
                  <button type="button" className="close" onClick={() => setShowBulkModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <label htmlFor="bulkStatusSelect">Select Status</label>
                  <select
                    id="bulkStatusSelect"
                    className="form-control"
                    value={bulkStatus}
                    onChange={(e) => setBulkStatus(e.target.value === 'true')}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowBulkModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleBulkUpdateStatus}>
                    Apply to {selectedCustomerIds.length} customer(s)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default ListCustomer;